import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminCustomerWishlist.css";

/* ─────────────────────────────────
   Product image helper
   ───────────────────────────────── */
function productImage(filename) {
  if (!filename) return null;
  return `${API_BASE}/uploads/shop/${filename}`;
}

/* ─────────────────────────────────
   Status Pill
   ───────────────────────────────── */
function StatusPill({ inStock }) {
  return (
    <span className={`awl-status-pill ${inStock ? "awl-status-pill--in" : "awl-status-pill--out"}`}>
      {inStock ? "● In Stock" : "○ Out of Stock"}
    </span>
  );
}

/* ─────────────────────────────────
   Wishlist Item Row
   ───────────────────────────────── */
function WishlistRow({ item, onDelete }) {
  const imgSrc = productImage(item.product_image);
  const price = item.sale_price
    ? parseFloat(item.sale_price)
    : parseFloat(item.product_price || 0);
  const fmt = (n) =>
    "R " + Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <tr className="awl-row">
      <td className="awl-td awl-td--product">
        <div className="awl-product-cell">
          {imgSrc ? (
            <img src={imgSrc} alt={item.product_name} className="awl-product-img" />
          ) : (
            <div className="awl-product-img-placeholder">🛒</div>
          )}
          <div className="awl-product-info">
            <p className="awl-product-name">{item.product_name || "—"}</p>
            {item.product_sku && (
              <p className="awl-product-sku">SKU: {item.product_sku}</p>
            )}
            {(item.car_make || item.car_model) && (
              <p className="awl-product-car">
                {[item.car_make, item.car_model, item.car_year_from ? `${item.car_year_from}–${item.car_year_to || ""}` : null]
                  .filter(Boolean)
                  .join(" ")}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="awl-td awl-td--customer">
        <p className="awl-customer-name">
          {item.customer_first_name} {item.customer_last_name}
        </p>
        <p className="awl-customer-email">{item.customer_email}</p>
      </td>
      <td className="awl-td awl-td--price">{fmt(price)}</td>
      <td className="awl-td awl-td--stock">
        <StatusPill inStock={parseInt(item.product_quantity) > 0} />
      </td>
      <td className="awl-td awl-td--category">
        {item.category_name ? (
          <span className="awl-category-tag">{item.category_name}</span>
        ) : (
          <span className="awl-muted">—</span>
        )}
      </td>
      <td className="awl-td awl-td--date">
        {item.wishlisted_at
          ? new Date(item.wishlisted_at).toLocaleDateString("en-ZA", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "—"}
      </td>
      <td className="awl-td awl-td--actions">
        <button
          className="awl-icon-btn awl-icon-btn--delete"
          title="Remove from wishlist"
          onClick={() => onDelete(item)}
        >
          ✕
        </button>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────
   Confirm Delete Dialog
   ───────────────────────────────── */
function ConfirmDialog({ item, onCancel, onConfirm, deleting }) {
  return (
    <div
      className="awl-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="awl-confirm">
        <div className="awl-confirm__icon">🗑</div>
        <h3 className="awl-confirm__title">Remove Wishlist Item?</h3>
        <p className="awl-confirm__msg">
          This will remove <strong>{item.product_name}</strong> from{" "}
          <strong>{item.customer_first_name} {item.customer_last_name}</strong>'s
          wishlist. This action cannot be undone.
        </p>
        <div className="awl-confirm__actions">
          <button className="awl-btn awl-btn--cancel" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className="awl-btn awl-btn--danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? "Removing..." : "Yes, Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminCustomerWishlist() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  /* filters */
  const [search,      setSearch]      = useState("");
  const [filterStock, setFilterStock] = useState("");
  const [sort,        setSort]        = useState("newest");

  /* pagination */
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const LIMIT = 20;

  /* delete modal */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  /* ── Load ── */
  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      sort,
      page,
      limit: LIMIT,
    });
    if (search)           params.set("search", search);
    if (filterStock !== "") params.set("in_stock", filterStock);

    apiFetch(`/api/admin/wishlist?${params}`)
      .then((data) => {
        setItems(data.items || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalItems(data.pagination?.total || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, filterStock, sort, page]);

  useEffect(() => { load(); }, [load]);

  /* reset to page 1 when filters change */
  useEffect(() => { setPage(1); }, [search, filterStock, sort]);

  /* ── Stats ── */
  const uniqueCustomers = new Set(items.map((i) => i.customer_id)).size;
  const uniqueProducts  = new Set(items.map((i) => i.product_id)).size;

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(
        `/api/admin/wishlist/${deleteTarget.customer_id}/${deleteTarget.product_id}`,
        { method: "DELETE" }
      );
      setSuccess(`Removed ${deleteTarget.product_name} from wishlist.`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout pageTag="CRM" pageTitle="Customer Wishlists">

      {/* ── Intro bar ── */}
      <div className="awl-intro-bar">
        <div className="awl-intro-bar__left">
          <span className="awl-intro-bar__icon">♡</span>
          <div>
            <h2 className="awl-intro-bar__title">Customer Wishlists</h2>
            <p className="awl-intro-bar__sub">
              View and manage products customers have saved to their wishlists.
            </p>
          </div>
        </div>
        <div className="awl-intro-bar__right">
          <span className="awl-stat-pill">
            <span className="awl-stat-pill__num">{totalItems}</span> Items
          </span>
          <span className="awl-stat-pill">
            <span className="awl-stat-pill__num">{uniqueCustomers}</span> Customers
          </span>
          <span className="awl-stat-pill">
            <span className="awl-stat-pill__num">{uniqueProducts}</span> Products
          </span>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="awl-alert awl-alert--error">⚠ {error}</div>}
      {success && <div className="awl-alert awl-alert--success">✓ {success}</div>}

      {/* ── Toolbar ── */}
      <div className="awl-toolbar">
        <div className="awl-toolbar__left">
          <div className="awl-search-wrap">
            <span className="awl-search-icon">⌕</span>
            <input
              className="awl-search"
              type="text"
              placeholder="Search product or customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="awl-filter-select"
            value={filterStock}
            onChange={(e) => setFilterStock(e.target.value)}
          >
            <option value="">All Stock Status</option>
            <option value="1">In Stock</option>
            <option value="0">Out of Stock</option>
          </select>
          <select
            className="awl-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="product_asc">Product A–Z</option>
            <option value="customer_asc">Customer A–Z</option>
            <option value="price_desc">Price High–Low</option>
            <option value="price_asc">Price Low–High</option>
          </select>
        </div>
        <div className="awl-toolbar__right">
          <span className="awl-count-label">
            {totalItems} item{totalItems !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="awl-skeleton" />
      ) : items.length === 0 ? (
        <div className="awl-empty">
          <div className="awl-empty__icon">♡</div>
          <h3 className="awl-empty__title">No Wishlist Items Found</h3>
          <p className="awl-empty__sub">
            {search || filterStock !== ""
              ? "Try adjusting your filters."
              : "Customers haven't added any products to their wishlists yet."}
          </p>
        </div>
      ) : (
        <div className="awl-table-wrap">
          <table className="awl-table">
            <thead>
              <tr>
                <th className="awl-th">Product</th>
                <th className="awl-th">Customer</th>
                <th className="awl-th">Price</th>
                <th className="awl-th">Stock</th>
                <th className="awl-th">Category</th>
                <th className="awl-th">Added</th>
                <th className="awl-th awl-th--actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <WishlistRow
                  key={`${item.customer_id}-${item.product_id}`}
                  item={item}
                  onDelete={(i) => setDeleteTarget(i)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="awl-pagination">
          <button
            className="awl-page-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ‹ Prev
          </button>
          <span className="awl-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="awl-page-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next ›
          </button>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <ConfirmDialog
          item={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

    </AdminLayout>
  );
}